import React, { useEffect, useRef, useState } from 'react';
import {
  PRICE_COST_WITHOUT_VAT,
  PRICE_RECOMMENDED_RETAIL,
} from '../constants/equipmentLabels';
import { auth, partnerStock as partnerStockApi } from '../api';
import { FiDownload, FiFilter, FiGrid, FiHash, FiList, FiPackage, FiSettings, FiTrash2, FiUpload } from 'react-icons/fi';
import EquipmentTable from '../components/EquipmentTable';
import { positionsLabel } from '../utils/pluralRu';

function PartnerStock() {
  const [items, setItems] = useState([]);
  const [user, setUser] = useState(null);
  const [partners, setPartners] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [replaceAll, setReplaceAll] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    loadData();
  }, [selectedPartner, selectedVendor, selectedWarehouse, rowsPerPage, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUser = async () => {
    try {
      setUser(await auth.getCurrentUser());
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [list, partnerNames, vendorNames, warehouseNames] = await Promise.all([
        partnerStockApi.getAll({
          partner: selectedPartner || undefined,
          vendor: selectedVendor || undefined,
          warehouse: selectedWarehouse || undefined,
          q: searchQuery || undefined,
          limit: rowsPerPage,
        }),
        partnerStockApi.getPartners(),
        partnerStockApi.getVendors(),
        partnerStockApi.getWarehouses(),
      ]);
      setItems(list);
      setPartners(
        (partnerNames || []).map((item) => (
          typeof item === 'string' ? { name: item, count: 0 } : item
        ))
      );
      setVendors(vendorNames);
      setWarehouses(warehouseNames);
      setSelectedCards(new Set());
    } catch (error) {
      console.error('Error loading partner stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCardSelection = (itemId) => {
    const next = new Set(selectedCards);
    if (next.has(itemId)) next.delete(itemId);
    else next.add(itemId);
    setSelectedCards(next);
  };

  const toggleSelectAllCards = () => {
    if (selectedCards.size === items.length) {
      setSelectedCards(new Set());
    } else {
      setSelectedCards(new Set(items.map((item) => item.id)));
    }
  };

  const downloadTemplate = async () => {
    try {
      const blob = await partnerStockApi.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'partner_stock_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.response?.data?.detail || 'Не удалось скачать шаблон');
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const confirmText = replaceAll
      ? 'Будет стёрт весь справочник ПАРТНЁРОВ и записан заново из файла. Основной склад АКМЕТРОН не изменится. Продолжить?'
      : 'Обновятся только компании из колонки «Партнёр» в файле. Остальные партнёры и основной склад не изменятся. Продолжить?';
    if (!window.confirm(confirmText)) return;

    setUploading(true);
    try {
      const result = await partnerStockApi.importExcel(file, replaceAll);
      alert(result.message || 'Справочник обновлён');
      await loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка загрузки файла');
    } finally {
      setUploading(false);
    }
  };

  const handleClear = async () => {
    if (selectedPartner) {
      if (!window.confirm(`Удалить все позиции партнёра «${selectedPartner}»? Основной склад не изменится.`)) {
        return;
      }
    } else if (!window.confirm('Удалить весь справочник партнёров? Основной склад АКМЕТРОН не изменится.')) {
      return;
    }
    try {
      const result = await partnerStockApi.clearAll(selectedPartner);
      alert(result.message || 'Справочник очищен');
      setSelectedPartner('');
      await loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const isAdmin = Boolean(user?.is_admin);

  return (
    <div className="px-2 sm:px-4 lg:px-6 xl:px-8">
      <div className="mb-4 sm:mb-6 lg:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Партнёры</h1>
        <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600">
          Справочник остатков партнёрских компаний — отдельно от склада АКМЕТРОН.
          Резервировать эти позиции нельзя. Можно держать несколько партнёров и фильтровать по названию.
        </p>
      </div>

      {isAdmin && (
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow mb-4 sm:mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <button
              type="button"
              onClick={downloadTemplate}
              className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FiDownload className="mr-2" />
              Скачать шаблон для партнёра
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              <FiUpload className="mr-2" />
              {uploading ? 'Загрузка...' : 'Загрузить Excel'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleUpload}
            />
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={replaceAll}
                onChange={(e) => setReplaceAll(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Заменить весь справочник
            </label>
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex items-center justify-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 lg:ml-auto"
            >
              <FiTrash2 className="mr-2" />
              Очистить {selectedPartner ? `«${selectedPartner}»` : 'весь справочник'}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Загрузка меняет только справочник партнёров, не основной склад.
            По умолчанию обновляются компании из файла (колонка «Партнёр»). Галочка «Заменить весь справочник» стирает всех партнёров.
          </p>
        </div>
      )}

      <div className="bg-white p-3 sm:p-4 rounded-lg shadow mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Фильтры:</span>
          </div>

          <select
            value={selectedPartner}
            onChange={(e) => setSelectedPartner(e.target.value)}
            className="flex-1 sm:flex-none min-w-[220px] border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Все партнёры</option>
            {partners.map((partner) => (
              <option key={partner.name} value={partner.name}>
                {partner.name}{partner.count ? ` (${partner.count})` : ''}
              </option>
            ))}
          </select>

          <select
            value={selectedVendor}
            onChange={(e) => setSelectedVendor(e.target.value)}
            className="flex-1 sm:flex-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Все вендоры</option>
            {vendors.map((vendor) => (
              <option key={vendor} value={vendor}>{vendor}</option>
            ))}
          </select>

          <select
            value={selectedWarehouse}
            onChange={(e) => setSelectedWarehouse(e.target.value)}
            className="flex-1 sm:flex-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Все склады</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse} value={warehouse}>{warehouse}</option>
            ))}
          </select>

          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск по всем полям..."
            className="flex-1 min-w-[220px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700 whitespace-nowrap">Строк на экране:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
            </select>
          </div>

          <div className="hidden lg:flex items-center border border-gray-300 rounded-lg">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                viewMode === 'grid' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FiGrid className="inline mr-1" />
              Карточки
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                viewMode === 'table' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FiList className="inline mr-1" />
              Таблица
            </button>
          </div>

          {viewMode === 'grid' && items.length > 0 && (
            <button
              type="button"
              onClick={toggleSelectAllCards}
              className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {selectedCards.size === items.length ? 'Снять выделение' : 'Выбрать все'}
            </button>
          )}

          <div className="text-sm text-gray-600 sm:ml-auto">
            Найдено: <strong>{items.length}</strong> {positionsLabel(items.length)}
          </div>
        </div>
        {partners.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedPartner('')}
              className={`px-3 py-1.5 text-sm rounded-full border ${
                !selectedPartner
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Все
            </button>
            {partners.map((partner) => (
              <button
                type="button"
                key={partner.name}
                onClick={() => setSelectedPartner(partner.name)}
                className={`px-3 py-1.5 text-sm rounded-full border ${
                  selectedPartner === partner.name
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {partner.name}
                {partner.count ? ` · ${partner.count}` : ''}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-xl text-gray-600">Загрузка...</div>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FiPackage className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Позиции не найдены</h3>
          <p className="mt-1 text-sm text-gray-500">
            {isAdmin ? 'Загрузите Excel от партнёра или измените фильтры' : 'Попробуйте изменить фильтры'}
          </p>
        </div>
      ) : viewMode === 'table' ? (
        <EquipmentTable
          equipmentList={items}
          selectedItems={selectedCards}
          setSelectedItems={setSelectedCards}
          allowBooking={false}
          settingsStorageKey="partnerStockTableSettings"
          exportEndpoint="/partner-stock/export"
          exportFilenamePrefix="partner_stock"
          exportIdField="item_ids"
          extraColumns={[{ key: 'partner_name', label: 'Партнёр', defaultVisible: true, width: 160 }]}
          hiddenColumnKeys={['available', 'in_transit', 'location']}
          title="Склады партнёров"
          emptyText="Позиции не найдены"
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((item) => (
            <div
              key={item.id}
              className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden ${
                selectedCards.has(item.id) ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedCards.has(item.id)}
                      onChange={() => toggleCardSelection(item.id)}
                      className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{item.name}</h3>
                      <p className="text-sm text-gray-600">
                        {item.vendor} {item.model && `• ${item.model}`}
                      </p>
                    </div>
                  </div>
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                    {item.partner_name}
                  </span>
                </div>

                {item.description && (
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">{item.description}</p>
                )}

                <div className="mt-4 space-y-2">
                  {item.warehouse && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">Склад:</span>
                      <span className="ml-2">{item.warehouse}</span>
                    </div>
                  )}
                  {item.configuration && (
                    <div className="flex items-center text-sm text-gray-600">
                      <FiSettings className="mr-2 h-4 w-4" />
                      <span className="truncate" title={item.configuration}>{item.configuration}</span>
                    </div>
                  )}
                  {item.price_ddp && (
                    <div className="flex items-center text-sm font-medium text-gray-900">
                      {PRICE_COST_WITHOUT_VAT}: {item.price_ddp.toLocaleString('ru-RU')} ₽
                    </div>
                  )}
                  {item.price_gpl && (
                    <div className="flex items-center text-sm font-medium text-gray-900">
                      Цена GPL: {item.price_gpl.toLocaleString('ru-RU')} CNY
                    </div>
                  )}
                  {item.price_retail != null && item.price_retail !== '' && (
                    <div className="flex items-center text-sm font-medium text-gray-900">
                      {PRICE_RECOMMENDED_RETAIL}: {item.price_retail.toLocaleString('ru-RU')} ₽
                    </div>
                  )}
                  {item.code_1c && (
                    <div className="flex items-center text-sm text-gray-600">
                      <FiHash className="mr-2 h-4 w-4" />
                      <span className="font-mono text-xs">{item.code_1c}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium">В наличии:</span>
                    <span className="ml-2">{item.quantity || 0} шт</span>
                  </div>
                  {item.comment && (
                    <div className="text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded mt-2">
                      <span className="font-medium">Комментарий:</span> {item.comment}
                    </div>
                  )}
                </div>
                <p className="mt-4 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                  Справочная позиция — бронирование недоступно
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PartnerStock;
